import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiDownload, FiEdit2, FiFilter, FiSearch, FiXCircle } from "react-icons/fi";

import Card from "components/Card";
import AppDataTable from "components/common/AppDataTable";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import { api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { apiURl } from "store/actions";
import UserChangeStatusModal from "pages/users/UserChangeStatusModal";

import StatusBadge from "components/common/StatusBadge";
import FormDatePicker from "components/form/FormDatePicker";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { buildQueryString, formatTs, nonEmptyStr } from "utils/helper";
import {
  normalizeUserStatus,
  USER_STATUS_OPTIONS,
  userStatusLabel,
} from "utils/usersList";

const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL_VALUES = {
  page: 1,
  limit: DEFAULT_LIMIT,
  search: "",
  investorType: "",
  status: "",
  date: "",
};

function mapInvestorToTableRow(inv) {
  const id = inv.id ?? inv._id ?? "";
  const profile = inv.individualProfile ?? inv.entityProfile ?? {};
  const fullName = (
    inv.full_name ??
    inv.fullName ??
    inv.name ??
    [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim()
  ) || inv.email?.split("@")[0] || "—";

  return {
    id: String(id),
    role: nonEmptyStr(inv.role),
    full_name: fullName,
    email: nonEmptyStr(inv.email),
    phone: nonEmptyStr(inv.phone),
    created_at: formatTs(inv.createdAt ?? inv.created_at),
    updated_at: formatTs(inv.updatedAt ?? inv.updated_at),
    status: nonEmptyStr(inv.status||inv?.accountStatus, "Inactive"),
    user_type: nonEmptyStr(inv.investorType),
    _raw: inv,
  };
}


function SrNoCell(row, index) {
  return <span className="font-semibold text-text-900">{index + 1}</span>;
}

function UserNameCell(row) {
  return <span className="font-semibold text-text-900">{row.full_name}</span>;
}

function UserStatusCell({ row, onEditStatus }) {
  return (
    <div
      className="inline-flex items-center gap-1.5"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <StatusBadge status={row.status} as="span" />
      <button
        type="button"
        title="Change status"
        aria-label={`Change status for ${row.full_name}`}
        onClick={() => onEditStatus(row)}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 text-text-600 transition hover:border-brand-400 hover:bg-slate-50 hover:text-brand-900"
      >
        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

UserStatusCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    full_name: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onEditStatus: PropTypes.func.isRequired,
};

export default function UsersPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [statusModalUser, setStatusModalUser] = useState(null);
  const [statusDraft, setStatusDraft] = useState("active");
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  const rows = useMemo(() => data?.data?.map(mapInvestorToTableRow) ?? [], [data]);
  const totalItems = useMemo(() => data?.total ?? 0, [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (data?.limit || filters.limit))),
    [totalItems, filters.limit, data]
  );

  useEffect(() => {
    setListError("");
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endUrl = `${apiURl.users}`;
      const qs = buildQueryString(filters);
      const path = qs ? `${endUrl}?${qs}` : endUrl;
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load users.";
        setListError(msg);
        swtAlert.error("Users", msg);
        return;
      }

      setData(resp);
      setListError("");
    } catch (error) {
      const msg = error?.message || "Could not load users.";
      setListError(msg);
      swtAlert.error("Users", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openStatusModal = useCallback((row) => {
    const current = normalizeUserStatus(row.status, "inactive");
    const defaultNextStatus =
      USER_STATUS_OPTIONS.find((option) => option.value !== current)?.value ?? "active";
    setStatusModalUser({
      id: row.id,
      full_name: row.full_name,
      email: row.email,
      status: row.status,
    });
    setStatusDraft(defaultNextStatus);
  }, []);

  const closeStatusModal = useCallback(() => {
    if (statusUpdating) return;
    setStatusModalUser(null);
  }, [statusUpdating]);

  const handleStatusUpdate = useCallback(async () => {
    if (!statusModalUser) return;

    const userId = String(statusModalUser.id);
    const nextStatus = normalizeUserStatus(statusDraft, "inactive");
    const currentStatus = normalizeUserStatus(statusModalUser.status, "inactive");

    if (nextStatus === currentStatus) {
      closeStatusModal();
      return;
    }

    setStatusUpdating(true);
    try {
      const resp = await api.patch(API_ENDPOINTS.adminInvestorStatus(userId), {
        status: nextStatus,
      });
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not update user status.";
        setStatusModalUser(null);
        await swtAlert.error("Update failed", msg);
        return;
      }

      setData((prev) => ({
        ...prev,
        data: (prev.data ?? []).map((inv) => {
          const id = String(inv.id ?? inv._id);
          if (id !== userId) return inv;
          return { ...inv, status: nextStatus, accountStatus: nextStatus };
        }),
      }));

      setStatusModalUser(null);
      await swtAlert.success(
        "Status updated",
        `User status changed to ${userStatusLabel(nextStatus)}.`,
      );
    } catch (error) {
      const msg = error?.message || "Could not update user status.";
      setStatusModalUser(null);
      await swtAlert.error("Update failed", msg);
    } finally {
      setStatusUpdating(false);
    }
  }, [statusModalUser, statusDraft, closeStatusModal]);

  const columns = useMemo(
    () => [
      { id: "sr_no", header: "#", cell: SrNoCell, sortable: true, width: "80px" },
      // { id: "id", header: "ID", accessor: "id", sortable: true },
      { id: "role", header: "Role", accessor: "role", sortable: true },
      { id: "user_type", header: "User Type", accessor: "user_type", sortable: true },
      {
        id: "full_name",
        header: "Name",
        accessor: "full_name",
        cell: UserNameCell,
        sortable: true,
      },
      { id: "email", header: "Email", accessor: "email", sortable: true },
      { id: "phone", header: "Phone", accessor: "phone", sortable: true },
      { id: "created_at", header: "Created At", accessor: "created_at", sortable: true },
      { id: "updated_at", header: "Updated At", accessor: "updated_at", sortable: true },
      {
        id: "status",
        header: "Status",
        cell: (row) => <UserStatusCell row={row} onEditStatus={openStatusModal} />,
        sortable: true,
      },
    ],
    [openStatusModal]
  );


  const exportToCsv = () => {
    const headers = [
      "id",
      "role",
      "user_type",
      "full_name",
      "email",
      "phone",
      "created_at",
      "updated_at",
      "status",
    ];
    const csvRows = rows.map((user) =>
      headers
        .map((header) => `"${String(user[header] ?? "").replaceAll('"', '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users-page-${filters.page}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = [filters.investorType, filters.status, filters.date].filter(Boolean).length;

  const usersManagementCardAction = (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={exportToCsv}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
        disabled={!rows.length}
      >
        <FiDownload className="h-3.5 w-3.5" />
        Export CSV
      </button>
    </div>
  );

  const usersFilterDrawer = filterDrawerOpen ? (
    <div className="mb-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <FormDatePicker
        label="Created date"
        name="users_list_date"
        value={filters.date}
        onChange={(event) => setFilters({ ...filters, date: event.target.value, page: 1 })}
        placeholder="Filter by date"
      />
      <div>
        <label htmlFor="filter-role" className="mb-1 block text-xs font-semibold text-text-500">
          User Type
        </label>
        <select
          id="filter-role"
          value={filters.user_type === "" ? "all" : filters.investorType}
          onChange={(e) => setFilters({ ...filters, investorType: e.target.value === "all" ? "" : e.target.value, page: 1 })}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none ring-brand-600 focus:ring-2"
        >
          <option value="all">All </option>
          <option value="individual">Individual</option>
          <option value="corporate">Corporate</option>
          
        </select>
      </div>
      <div>
        <label htmlFor="filter-status" className="mb-1 block text-xs font-semibold text-text-500">
          Status
        </label>
        <select
          id="filter-status"
          value={filters.status === "" ? "all" : filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value === "all" ? "" : e.target.value, page: 1 })}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none ring-brand-600 focus:ring-2"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          
          <option value="inactive">Inactive</option>
        </select>
      </div>
    </div>
  ) : null;

  return (
    <>
      <UserChangeStatusModal
        open={Boolean(statusModalUser)}
        user={statusModalUser}
        statusDraft={statusDraft}
        updating={statusUpdating}
        onStatusChange={setStatusDraft}
        onClose={closeStatusModal}
        onUpdate={handleStatusUpdate}
      />

      <AppBreadcrumbs items={[{ label: "Users", path: "/users" }]} />
      <Card title="User Management" action={usersManagementCardAction} className="mb-4" />
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-text-900">Users List</h3>
            <p className="text-xs text-text-500">Total investors: {totalItems}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    flushSearch();
                    setFilters((prev) => ({
                      ...prev,
                      search: searchDraft.trim(),
                      page: 1,
                    }));
                  }
                }}
                placeholder="Search users..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
              />
            </div>

            <button
              type="button"
              aria-label="Open filters"
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen((open) => !open)}
              className="relative rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiFilter />
              {activeFilterCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-900 px-0.5 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            {(searchDraft || filters.investorType || filters.status || filters.date) && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Clear filters"
                  onClick={() => {
                    setSearchDraft("");
                    setFilters(FILTER_INITIAL_VALUES);
                  }}
                  className="relative rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
                >
                  <FiXCircle />
                </button>
              </div>
            )}
          </div>
        </div>
        {usersFilterDrawer}

        {listError ? (
          <ListFetchErrorBanner message={listError} onRetry={fetchData} loading={loading} />
        ) : null}

        <AppDataTable
          columns={columns}
          data={rows}
          rowKey="id"
          loading={loading}
          onRowClicked={(row) =>
            navigate(`/users/${row.id}`, { state: { user: row._raw } })
          }
        />

        <AppPagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(p) => setFilters({ ...filters, page: p })}
          totalItems={totalItems}
          pageSize={filters.limit}
          onPageSizeChange={(value) => {
            setFilters({ ...filters, limit: value, page: 1 });
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

      

        
      </Card>
    </>
  );
}
